import { createRef } from 'react'
import {
  compose,
  withHandlers,
  withState,
  renameProp,
  withProps,
  lifecycle,
  branch,
  renderNothing,
} from 'recompose'
import * as R from 'ramda'
import { connect } from 'react-redux'
import { change } from 'redux-form/immutable'
import { geocodeByAddress } from 'react-places-autocomplete'
import { getZipFromLatLon } from 'sidecar-utilities'
import toReduxFormField from '../../../enhancers/ToReduxFormField'

import { getData, isFiveDigitNumString } from './utils'

const enhance = compose(
  connect(
    ({ SearchFilters }) => ({
      //TODO: Change to only use non-Immutable method after Admin is converted
      searchZip:
        SearchFilters?.getIn?.(['filters', 'searchLocation', 'zip']) ||
        SearchFilters?.filters?.searchLocation?.zip,
    }),
    { change },
  ),
  branch(({ searchZip }) => R.isNil(searchZip), renderNothing),
  withState('address', 'setAddress', ''),
  lifecycle({
    componentDidMount() {
      const { props } = this
      props.setAddress(props.searchZip)
    },
  }),
  withState('locError', 'setLocError', ''),
  renameProp('onChange', 'onChangeCallback'),
  toReduxFormField('InputLocation'),
  withProps(({ address }) => {
    // only search/find addresses in the United States
    const searchOptions = { componentRestrictions: { country: 'us' } }
    return {
      // we're going to create a reference to the PlacesAutocomplete
      // so that when the input is blurred, we have a way to trigger
      // the select event for that component (we need it to clear suggestions).
      placesRef: createRef(),
      // check if address is just a 5 digit number, if so we'll change
      // react-places-autocomplete searchOptions to return zip code
      searchOptions: isFiveDigitNumString(address)
        ? { ...searchOptions, types: ['(regions)'] }
        : searchOptions,
    }
  }),
  withHandlers({
    // noop function to prevent [Object Object] from being
    // displayed in address field if user leaves it blank.
    handleOnBlurDefault: () => R.identity,
    handleChange: props => address => {
      props.setAddress(address)
      if (props.locError) {
        props.setLocError('')
      }
    },
    handleOnSelect: props => async selectedAddress => {
      // doing this blur makes sure the keyboard goes away
      // this was the only way to blur the InputText within
      // the PlacesAutocomplete. Trying to have 2 refs was
      // causing errors to be thrown.
      document.activeElement.blur()

      // getting geocode might fail even if the string is valid.
      // "74757" is an invalid zipcode. use it to test.
      let geocode
      try {
        geocode = await geocodeByAddress(selectedAddress)
      } catch (err) {
        props.setLocError('Invalid location')
        return
      }

      // Google's API can be kinda nutty so we'll pull out values
      // in a way that we'll find useful in our own application.
      const getPlaceData = getData(geocode[0].address_components)
      const address = geocode[0].formatted_address
      const street = `${getPlaceData('street_number')} ${getPlaceData('route')}`
      const city =
        getPlaceData('administrative_area_level_3') ||
        getPlaceData('locality') ||
        getPlaceData('sublocality_level_1')
      const state = getPlaceData('administrative_area_level_1', 'short_name')
      const lat = geocode[0].geometry.location.lat()
      const lon = geocode[0].geometry.location.lng() // eww

      // we really need to get a zip code. If a user enters a city or something
      // there won't really be , but we can get one from lat/lon info.
      let zip = getPlaceData('postal_code')
      if (!zip) {
        const zipLatLon = await getZipFromLatLon(lat, lon)
        zip = zipLatLon.zip
      }
      // if we still don't have a zip, we need to trigger a validation error
      // because if we try to send this off to our API, it'll barf. Still change
      // the field though so that the user can see what they chose.
      if (!zip) {
        props.input.onChange(address)
        props.setLocError('Invalid location')
        return
      }

      // replaces the value in the input with the formatted
      // address the user just selected from the list.
      props.input.onChange(address)
      props.onChangeCallback && props.onChangeCallback(address)

      // update the values in hidden form fields.
      // this is done so that address info is easily
      // available as extra values when the form submits.
      const formName = props.meta.form
      props.change(formName, 'street', street)
      props.change(formName, 'city', city)
      props.change(formName, 'state', state)
      props.change(formName, 'zip', zip)
      props.change(formName, 'lat', lat)
      props.change(formName, 'lon', lon)

      // this triggers the onSelect handler that may
      // have been passed to this component. We'll
      // pass back the derived data for easy extraction.
      !!props.onSelect &&
        props.onSelect({
          address,
          street,
          city,
          state,
          zip,
          lat,
          lon,
        })
    },
  }),
)

export default enhance
